import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import {
  CELL_RENDERERS,
  Checkbox,
  IconInfo,
  Link,
  Switch,
  TableContainer,
  TableContent,
  TableFooter,
  TableHeader,
  TextRenderer,
  THEMES,
  withTooltip
} from 'cdk-radial';

import { METADATA_CONSTANTS } from '../../../../../constants/metadataConstant';

import './index.scss';

const { REQUESTED_STATUS: { ENABLE, DISABLE } } = METADATA_CONSTANTS;

const VerticalCenterContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FullWidthCellBase = styled(VerticalCenterContainer)`
    width: 100%;
    justify-content: space-between;
`;

const CenterCellBase = styled(FullWidthCellBase)`
    justify-content: center;
`;

const SmallIconInfo = styled(IconInfo)`
    width: 1.125rem;
    height: 1.125rem;
    opacity: 0.7;
`;

const ExplainerOnHover = styled.span`
    text-decoration: underline dotted;
    cursor: help;
`;

const frameworkComponentsExtended = {
  textRenderer: TextRenderer
};

/**
 * @type import('ag-grid-community').ColDef
 */
const nameColumn = {
  cellRenderer: CELL_RENDERERS.TEXT,
  field: 'partyName',
  headerName: 'Dealership Location',
  sortable: true
};
/**
 * @type import('ag-grid-community').ColDef
 */
const addressColumn = {
  cellRenderer: CELL_RENDERERS.TEXT,
  field: 'address',
  headerName: 'Address',
  sortable: true
};
/**
 * @type import('ag-grid-community').ColDef
 */
const cppColumn = {
  cellRendererFramework: LocationCppSwitch,
  headerComponentFramework: CppColumnHeader,
  headerName: 'Privacy Protection',
  headerComponentParams: {
    explanatoryText: "Restrict the app's access to customers' personal data through the API."
  },
  minWidth: 180,
  width: 180,
  suppressSizeToFit: true
}
/**
 * @type import('ag-grid-community').ColDef
 */
const intStatusColumn = {
  cellRendererFramework: LocationStatusSwitch,
  headerComponentFramework: TableHeaderWithInfo,
  headerName: 'Integration Status',
  headerComponentParams: {
    explanatoryText: "Enable or disable the app's access to the API."
  },
  minWidth: 180,
  width: 180,
  suppressSizeToFit: true
}
/**
 * @type import('ag-grid-community').ColDef
 */
const provStatusColumn = {
  cellRendererFramework: ProvisioningStatusCell,
  headerComponentFramework: TableHeaderWithInfo,
  field: 'apiProvisioningStatus',
  headerName: 'Provisioning Status',
  headerComponentParams: {
    explanatoryText: 'Indicates if the API Publisher has provisioned your access to the Async API.'
  },
  minWidth: 180,
  width: 180,
  suppressSizeToFit: true
}

const restColumns = [nameColumn, addressColumn, intStatusColumn];
const restCppColumns = [nameColumn, addressColumn, cppColumn, intStatusColumn];
const asyncColumns = [nameColumn, addressColumn, intStatusColumn, provStatusColumn];
const asyncCppColumns = [nameColumn, addressColumn, cppColumn, intStatusColumn, provStatusColumn];

function LocationCppSwitch({ data }) {
  const { index, cppOptOut } = data;
  return <CenterCellBase>
    <Checkbox
      checked={cppOptOut}
      hideLabel
      label="Consumer Privacy Protection"
      name={`locations__cpp-${index}`}
      dataTestId={`locations__cpp-${index}`}
      onChange={data.updateCppOptOut}
    />
  </CenterCellBase>;
}

function LocationStatusSwitch({ data, context }) {
  const { index, disabled, checkedStatus, updatedRequestedStatus, toggleHandler } = data;
  const { isAsyncApi } = context;
  const switchCaption = disabled
    ? isAsyncApi
      ? `API is ${updatedRequestedStatus} for location`
      : (`Pending request for ${checkedStatus ? ENABLE : DISABLE}`)
    : '';

  return <CenterCellBase>
    <Checkbox
      checked={checkedStatus}
      hideLabel
      label='Integration Status'
      name={`locations__switch-${index}`}
      dataTestId={`locations__switch-${index}`}
      isDisabled={disabled}
      onChange={e => {
        toggleHandler(e, index);
      }}
      title={switchCaption}
    />
  </CenterCellBase>;
}

function TextWithTooltip({ content, tooltipText }) {
  const TooltipWrapper = withTooltip(ExplainerOnHover, {
    id: 'text-info-tooltip',
    text: tooltipText
  });

  return <TooltipWrapper>{content}</TooltipWrapper>;
}

function ProvisioningStatusCell({ data }) {
  switch (data.apiProvisioningStatus) {
    case 'inactive':
      return <TextWithTooltip
        content='Inactive'
        tooltipText='The integration is disabled, or the API Publisher has not started provisioning.'
      />;
    case 'pending':
      return <TextWithTooltip
        content='Pending'
        tooltipText='The API Publisher is working on provisioning. The app may still receive events if the API Publisher sends them.'
      />;
    case 'active':
      return <TextWithTooltip
        content='Active'
        tooltipText='The API Publisher has finished provisioning and is able to send events.'
      />;
    default:
      return 'Unknown';
  }
}

function CppColumnHeader({
  displayName,
  explanatoryText
}) {
  const TooltipWrapper = withTooltip(SmallIconInfo, {
    id: 'header-info-tooltip',
    text: explanatoryText,
    theme: THEMES.LIGHT
  });

  return <FullWidthCellBase>
    <span role='columnheader' className='ag-header-cell-text'>{displayName}</span>
    <TooltipWrapper />
  </FullWidthCellBase>;
}

function TableHeaderWithInfo({ displayName, explanatoryText }) {
  const TooltipWrapper = withTooltip(SmallIconInfo, {
    id: 'header-info-tooltip',
    text: explanatoryText,
    theme: THEMES.LIGHT
  });

  return <FullWidthCellBase>
    <span role='columnheader' className='ag-header-cell-text'>{displayName}</span>
    <TooltipWrapper />
  </FullWidthCellBase>;
}

export default function LocationsTable({
  apiName,
  locations,
  isAsyncApi,
  cppSupported
}) {
  const [searchValue, setSearchValue] = useState('');
  const [gridApi, setGridApi] = useState();

  const columns = useMemo(() => {
    if (isAsyncApi) {
      return cppSupported ? asyncCppColumns : asyncColumns;
    }
    return cppSupported ? restCppColumns : restColumns;
  }, [isAsyncApi, cppSupported]);

  const handleGridReady = useCallback(gridParams => {
    setGridApi(gridParams.api);
    gridParams.api.sizeColumnsToFit();
  }, []);

  const context = { isAsyncApi };

  return (
    <TableContainer className='integration-locations-table ag-theme-balham'>
      <TableHeader
        id='integration-locations-table-header'
        searchInputName='integration-locations-search'
        headerTitle={apiName}
        searchInputValue={searchValue}
        onSearchInputChange={v => {
          setSearchValue(v);
          gridApi.setQuickFilter(v);
        }}
        searchPlaceholder='Find a location by name'
        isPrintable={false}
        isDownloadable={false}
      />
      <TableContent>
        <AgGridReact
          // force grid to rebuild when columns change
          key={`${columns.map(c => c.headerName)}`}
          columnDefs={columns}
          rowData={locations}
          context={context}
          frameworkComponents={frameworkComponentsExtended}
          onGridReady={handleGridReady}
          rowHeight={40}
          domLayout='autoHeight'
        />
      </TableContent>
      <TableFooter />
    </TableContainer>
  );
}
